import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { BrowserAuthError, BrowserAuthErrorCodes } from '@azure/msal-browser';
import { switchMap } from 'rxjs';
import Swal, { SweetAlertIcon } from 'sweetalert2';

import { AuthService } from './auth.service';
import { ManageAccountService } from '../api-lab4u/user-auth/manage-account.service';
import {
  LOGIN_TYPE_DISPLAY_NAME,
  LoginAttempt,
  LoginType,
} from './models/auth-login.models';

type StatusHandler = (attempt: LoginAttempt) => void;

/**
 * Translates a failed login attempt into the dialog the user should see.
 * To support a new HTTP status, add an entry to `handlersByStatus`.
 */
@Injectable({ providedIn: 'root' })
export class LoginErrorHandlerService {
  private readonly authService = inject(AuthService);
  private readonly manageAccountService = inject(ManageAccountService);
  private readonly transloco = inject(TranslocoService);
  private readonly router = inject(Router);

  private readonly handlersByStatus: Partial<Record<number, StatusHandler>> = {
    //* User exists, credentials invalid
    401: (attempt) =>
      attempt.loginType === 'LAB4U'
        ? this.explainInvalidCredentials(attempt.email)
        : // SSO 401 means an IdP/backend inconsistency, a hacking attempt or a backend bug
          this.ssoAlert(
            'SIGN-IN.ERROR.DESCRIPTION',
            attempt.loginType,
            'error',
          ),
    //* User doesn't exist in DB
    404: (attempt) =>
      attempt.loginType === 'LAB4U'
        ? this.offerSignUp()
        : //TODO If the User was attempting SSO, register them (API v3 - Register) and let them in
          this.ssoAlert(
            'SIGN-IN.ERROR.NOT-REGISTER-DESCRIPTION',
            attempt.loginType,
            'warning',
          ),
  };

  handle(err: unknown, attempt: LoginAttempt): void {
    if (!(err instanceof HttpErrorResponse)) {
      //* Provider side error (e.g. MSAL popup); a closed popup is not an error for the user
      if (this.isUserCancellation(err)) return;
      console.error(`${attempt.loginType} login error`, err);
      this.genericError();
      return;
    }

    const handler = this.handlersByStatus[err.status];
    if (handler) handler(attempt);
    else this.genericError();
  }

  /** If the User attempted a LAB4U login, check whether they registered with SSO and warn them */
  private explainInvalidCredentials(email?: string): void {
    this.authService
      .userExists({ email })
      .pipe(
        switchMap((userId) =>
          this.manageAccountService.getUserLoginTypes({ user_id: userId }),
        ),
      )
      .subscribe({
        next: ({ login_types: loginTypes }) => {
          if (!loginTypes?.length) {
            this.genericError();
          } else if (loginTypes.some((lt) => lt.name === 'LAB4U')) {
            //* User registered with LAB4U, bad password
            this.alert(
              'Error',
              this.transloco.translate('SIGN-IN.ERROR.INCORRECT-PASSWORD'),
              'error',
            );
          } else {
            //* User registered with SSO
            const params = { username: this.displayName(loginTypes[0].name) };
            this.alert(
              this.transloco.translate(
                'SIGN-IN.ERROR.SSO-REGISTER-TITLE',
                params,
              ),
              this.transloco.translate(
                'SIGN-IN.ERROR.SSO-REGISTER-MESSAGE',
                params,
              ),
              'warning',
            );
          }
        },
        error: () => this.genericError(),
      });
  }

  /** CTA that redirects to the Register page */
  private offerSignUp(): void {
    Swal.fire({
      title: 'Error',
      text: this.transloco.translate('SIGN-IN.ERROR.NOT-REGISTER-TITLE'),
      icon: 'error',
      showCancelButton: false,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Ok',
      customClass: {
        popup: 'custom-swal-modal',
      },
    }).then((result) => {
      if (result.value) {
        this.router.navigate(['/sign-up']);
      }
    });
  }

  private ssoAlert(
    key: string,
    loginType: LoginType,
    icon: SweetAlertIcon,
  ): void {
    const message = this.transloco.translate(key, {
      username: this.displayName(loginType),
    });
    this.alert('Error', message, icon);
  }

  private genericError(): void {
    this.alert(
      'Error',
      this.transloco.translate('SIGN-IN.ERROR.TITLE'),
      'error',
    );
  }

  private alert(title: string, text: string, icon: SweetAlertIcon): void {
    Swal.fire(title, text, icon);
  }

  private displayName(loginTypeName: string): string {
    return LOGIN_TYPE_DISPLAY_NAME[loginTypeName as LoginType] ?? loginTypeName;
  }

  private isUserCancellation(err: unknown): boolean {
    return (
      err instanceof BrowserAuthError &&
      err.errorCode === BrowserAuthErrorCodes.userCancelled
    );
  }
}
